import type { AgentState } from '../api/admin.ts'
import type { Tone } from '../../components/primitives/Metric.tsx'

/**
 * O estado de um agente segundo a varredura de silêncio (§4.2), para a tela.
 *
 * Mesma hierarquia do painel de saúde da frota: `anomalous_silence` é achado de
 * segurança (o agente parou dentro da janela em que costuma reportar); os outros
 * silêncios são manutenção. `unknown` é só "antes da primeira varredura".
 */
interface StateSpec {
  tone: Tone
  label: string
  note: string
  /** Menor = mais urgente. */
  rank: number
}

const SPEC: Record<string, StateSpec> = {
  anomalous_silence: {
    tone: 'crit',
    label: 'silêncio anômalo',
    note: 'Parou de reportar dentro da janela ativa histórica.',
    rank: 0,
  },
  never_connected: {
    tone: 'warn',
    label: 'nunca conectou',
    note: 'Provisionado e nunca reportou.',
    rank: 1,
  },
  expected_offline: {
    tone: 'neutral',
    label: 'offline esperado',
    note: 'Silencioso fora da janela histórica, ou ainda sem baseline confiável.',
    rank: 2,
  },
  connected: {
    tone: 'ok',
    label: 'conectado',
    note: 'Reportando normalmente.',
    rank: 3,
  },
  unknown: {
    tone: 'neutral',
    label: 'sem varredura',
    note: 'Ainda sem varredura de silêncio.',
    rank: 4,
  },
}

export function agentStatePresentation(state: AgentState): StateSpec {
  // Vocabulário aberto: valor novo vai como veio, sem cor.
  return SPEC[state] ?? { tone: 'neutral', label: state, note: 'Estado não reconhecido por esta versão.', rank: 5 }
}

export function agentStateLabel(state: AgentState): string {
  return agentStatePresentation(state).label
}

/** Estados de silêncio: os que trazem `silent_for_minutes`. */
export function isSilentState(state: AgentState): boolean {
  return state === 'anomalous_silence' || state === 'never_connected' || state === 'expected_offline'
}
